import { FC, useCallback } from 'react';
import { ArticleContent } from '@article/body/main/article-content';
import { useAtomValue, useSetAtom } from 'jotai';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { useTranslation } from 'react-i18next';
import { useTimeAgoFormatter } from '~/utils/format';
import { useViewTracking } from '~/utils/use-view-tracking';
import { Link } from '@remix-run/react';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { $CurrentArticleTranslated } from '@article/shared/atoms/current-article-translated';
import { LegacyHashtagItem } from '@article/shared/components/legacy-hashtag-item';
import ChevronDownFill from 'icon/chevron_down_fill.svg';
import { $CurrentArticleFold, $CurrentArticleHeightComputed } from '@article/shared/atoms/current-article-fold';
import cls from 'classnames';
import { PartialArticleContent } from '../partial-article-content/index';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { useScopedAtom, useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { ArticleContentSkeleton } from '~/skeleton/article/component';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { SAR } from '~/components/article-sar';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { RelatedExperience } from '~/routes/$user_link_name_.$article_id/shared/components/related-experience';
import { GptSupplements } from '~/routes/$user_link_name_.$article_id/shared/components/gpt-supplements';
import { $EnableCici } from '~/models/enable-cici';

import './style.scss';
import './poi.scss';

const ArticleEditTime: FC = () => {
  const { t } = useTranslation();
  const formatTimeAgo = useTimeAgoFormatter();
  const { publishTime, modifyTime } = useAtomValue($CurrentArticleAsAvailable);

  const timeAgo = formatTimeAgo(modifyTime || publishTime);
  // add description for re-edit;
  const des = (Boolean(modifyTime) && t('article_bottom_time')) || '';
  // add space for slash-formatted-time and re-edited article
  const sep = timeAgo.includes('/') && des ? ' ' : '';

  return (
    <div className="article-edit-time">
      <div className="time">{`${timeAgo}${sep}${des}`}</div>
    </div>
  );
};

const ArticlePOI: FC = () => {
  const { locationInfo, articleRegion } = useAtomValue($CurrentArticleAsAvailable);
  const activeToCurrentArticle = useActiveToCurrentArticle();

  const poiObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.main_poi,
      });
    }, []),
  });

  const handlePoiClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.main_poi);
      },
      clickEvent: () => ({
        current_position: ClickPosition.main_poi,
        page_click_id: locationInfo?.poiId || '',
      }),
    },
    [activeToCurrentArticle],
  );

  if (!locationInfo) {
    return null;
  }

  const { poiId, poiName } = locationInfo;

  return (
    <Link
      to={`/poi/${poiId}?region=${articleRegion}`}
      ref={poiObservedRef}
      className="poi-container"
      onClick={handlePoiClick}
    >
      <div className="poi-container-img" />
      <div className="poi-container-name">{poiName}</div>
    </Link>
  );
};

const LegacyForumList: FC = () => {
  const { forumList } = useAtomValue($CurrentArticleAsAvailable);

  if (!forumList || !forumList.length) {
    return null;
  }

  return (
    <div className="article-legacy-forum-list">
      {forumList.map((f) => (
        <LegacyHashtagItem key={f.id} hashtag={f} />
      ))}
    </div>
  );
};

const ArticleReadMoreBanner: FC = () => {
  const activeToCurrentArticle = useActiveToCurrentArticle();
  const setSimpleCTAModel = useSetAtom($SimpleCTAModalShow);
  const enableCici = useAtomValue($EnableCici);
  const { t } = useTranslation();
  const computed = useScopedAtomValue($CurrentArticleHeightComputed);
  const [folded, setFolded] = useScopedAtom($CurrentArticleFold);

  const readMoreBtnRef = useViewTracking<HTMLDivElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_position: ClickPosition.main_content_read_more,
      });
    }, []),
  });

  const handleClickModalConfirm = useReflowClick(
    {
      reflowBehavior: () => activeToCurrentArticle(ClickPosition.main_content_read_more_modal),
      defaultBehavior: () => activeToCurrentArticle(ClickPosition.main_content_read_more_modal),
    },
    [activeToCurrentArticle],
  );

  const handleClickModalCancel = useCallback(() => {
    if (enableCici) {
      setFolded(false);
    }
  }, [enableCici, setFolded]);

  const handleReadMoreClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.main_content_read_more,
    });
    setSimpleCTAModel({
      currentPosition: ClickPosition.main_content_read_more_modal,
      body: t('seo_nextPic_modal_desc'),
      handleClickConfirm: handleClickModalConfirm,
      handleClickCancel: handleClickModalCancel,
    });
  }, [handleClickModalConfirm]);

  return (
    <div
      className={cls('article-content-read-more-banner')}
      style={{
        display: folded ? void 0 : 'none',
        visibility: folded && computed ? 'visible' : 'hidden',
      }}
    >
      <div className="read-more-btn" ref={readMoreBtnRef} onClick={handleReadMoreClick}>
        <div className="read-btn-desc">{t('read_more')}</div>
        <div className="read-btn-arrow-right">
          <ChevronDownFill />
        </div>
      </div>
    </div>
  );
};

const PartialArticleLoading: FC = () => {
  const computed = useScopedAtomValue($CurrentArticleHeightComputed);
  const folded = useScopedAtomValue($CurrentArticleFold);
  return (
    <div
      className="lemon8-partial-article-loading"
      style={{
        display: folded ? void 0 : 'none',
        visibility: folded && computed ? 'hidden' : 'visible',
      }}
    >
      <ArticleContentSkeleton />
    </div>
  );
};

export const WapArticleSection: FC = () => {
  const article = useAtomValue($CurrentArticleAsAvailable);
  const { title, articleRegion } = article;
  const translated = useAtomValue($CurrentArticleTranslated);
  const isMobileUser = useAtomValue($IsMobileUser);
  const folded = useScopedAtomValue($CurrentArticleFold);
  const { isBot } = useAtomValue($UserAgent);

  return (
    <section id="article-content">
      {title && <h1 className="wap-article-title">{title}</h1>}
      {isMobileUser ? (
        <>
          <div className="partial-article-container">
            <PartialArticleContent />
            <ArticleReadMoreBanner />
            <PartialArticleLoading />
          </div>
          <div
            id="real-content"
            style={{
              display: folded ? 'none' : void 0,
            }}
          >
            <ArticleContent />
          </div>
        </>
      ) : (
        <>
          <ArticleContent />
          {!translated && <ArticlePOI />}
          {!translated && <LegacyForumList />}
        </>
      )}
      <GptSupplements />
      <ArticleEditTime />
      <SAR currentArticle={article} />
      {isBot && <RelatedExperience articleRegion={articleRegion} />}
    </section>
  );
};
